import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Match, MatchStatus } from '@/types/admin'
import { Edit, Trash } from 'lucide-react'
import { format } from 'date-fns'

interface MatchesTableProps {
  matches: Match[]
  onEdit: (match: Match) => void
  onDelete: (id: string) => void
}

const statusMap: Record<MatchStatus, { label: string; color: string }> = {
  sent: {
    label: 'Enviado',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
  },
  interview: {
    label: 'Entrevista',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  },
  hired: {
    label: 'Contratado',
    color: 'bg-green-100 text-green-800 border-green-200',
  },
  rejected: {
    label: 'Não Avançou',
    color: 'bg-red-100 text-red-800 border-red-200',
  },
}

export function MatchesTable({ matches, onEdit, onDelete }: MatchesTableProps) {
  return (
    <div className="rounded-md border border-slate-200 bg-white shadow-sm overflow-hidden">
      <Table>
        <TableHeader className="bg-slate-50">
          <TableRow>
            <TableHead className="font-semibold text-slate-700">
              Vaga / Escola
            </TableHead>
            <TableHead className="font-semibold text-slate-700">
              Educador
            </TableHead>
            <TableHead className="font-semibold text-slate-700">
              Data Criação
            </TableHead>
            <TableHead className="font-semibold text-slate-700">
              Status
            </TableHead>
            <TableHead className="text-right font-semibold text-slate-700">
              Ações
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {matches.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={5}
                className="text-center py-10 text-slate-500"
              >
                Nenhum match encontrado.
              </TableCell>
            </TableRow>
          ) : (
            matches.map((match) => (
              <TableRow key={match.id} className="hover:bg-slate-50/50">
                <TableCell>
                  <div className="font-medium text-slate-900">
                    {match.jobs?.position || 'Vaga Removida'}
                  </div>
                  <div className="text-xs text-slate-500">
                    {match.jobs?.school_name || 'Escola Removida'}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="font-medium text-slate-900">
                    {match.educators?.full_name || 'Educador Removido'}
                  </div>
                  <div className="text-xs text-slate-500">
                    {match.educators?.email}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-slate-600">
                  {format(new Date(match.created_at), 'dd/MM/yyyy')}
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={`font-normal ${statusMap[match.status]?.color || ''}`}
                  >
                    {statusMap[match.status]?.label || match.status}
                  </Badge>
                  {match.internal_notes && (
                    <div
                      className="text-[10px] text-slate-400 mt-1 max-w-[150px] truncate"
                      title={match.internal_notes}
                    >
                      {match.internal_notes}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(match)}
                      title="Editar Status / Notas"
                    >
                      <Edit className="h-4 w-4 text-slate-500" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => onDelete(match.id)}
                      title="Excluir"
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}
